import { useCallback, useEffect, useRef, useState } from "react";

import {
  useAITurn,
  useManualEvaluate,
  useSessionAITurns,
  type AIMessage,
  type AITurnUsage,
  type ManualEvaluateResponse,
} from "@/lib/api/ai-refill";
import type { ManualModeReason } from "@/components/refill/manual-mode-banner";

// RefillConversation is the single per-session AI thread shared across the
// whole refill flow: the wizard's analysis (step 2), prioritized actions
// (step 3), per-dose mixing feedback (step 5), the close-out summary (step 8),
// and the free-form assistant panel all append to ONE continuous conversation.
// The detail page reuses the same hook so its assistant trail is continuous too.
//
// Durability: the thread is seeded once from the server-persisted turns
// (useSessionAITurns → llm_calls), so it survives navigation AND full reloads.
// After seeding, local state is authoritative — each turn replaces it with the
// server's returned history (which includes the user message + assistant reply).
export interface RefillConversation {
  history: AIMessage[];
  // run appends a user turn and posts it with the full prior history, so the
  // model always sees the continuous context. Used by step CTAs and the panel.
  run: (prompt: string) => void;
  isRunning: boolean;
  error: string | null;
  // When the AI is unavailable / over budget, the flow drops to manual mode and
  // surfaces the engine output instead. Null means AI is live.
  manualMode: ManualModeReason | null;
  manualData: ManualEvaluateResponse | undefined;
  toggleManual: () => void;
  usage: AITurnUsage | undefined;
  // True until the persisted thread has loaded (so callers can avoid a flash of
  // "empty conversation" before the seed lands).
  loading: boolean;
}

export function useRefillConversation(
  sessionID: string,
): RefillConversation {
  const turn = useAITurn(sessionID);
  const manual = useManualEvaluate(sessionID);
  const persisted = useSessionAITurns(sessionID);

  const [history, setHistory] = useState<AIMessage[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [manualMode, setManualMode] = useState<ManualModeReason | null>(null);

  // Keep the latest history in a ref so `run` stays identity-stable (it doesn't
  // need `history` in its deps) and never closes over a stale thread.
  const historyRef = useRef<AIMessage[]>([]);
  useEffect(() => {
    historyRef.current = history;
  }, [history]);

  // Seed exactly once — from the persisted thread when it loads, OR implicitly
  // the first time the user runs a turn. A post-turn refetch of the persisted
  // query must never clobber the live local thread, so user interaction also
  // trips the guard.
  const seededRef = useRef(false);
  useEffect(() => {
    if (seededRef.current) return;
    if (persisted.data) {
      seededRef.current = true;
      if (persisted.data.history?.length) setHistory(persisted.data.history);
    }
  }, [persisted.data]);

  const run = useCallback(
    (prompt: string) => {
      const text = prompt.trim();
      if (!text || isRunning) return;
      seededRef.current = true;
      const base = historyRef.current;
      const userMsg: AIMessage = {
        role: "user",
        content: [{ type: "text", text }],
      };
      setError(null);
      setManualMode(null);
      setIsRunning(true);
      // Optimistically show the user's message (and the "Thinking…" state)
      // before the round-trip settles; rolled back on error.
      setHistory([...base, userMsg]);
      turn.mutate(
        { message: text, history: base },
        {
          onSuccess: (res) => {
            setHistory(res.history);
            setIsRunning(false);
          },
          onError: (err) => {
            const code = (err as { code?: string }).code;
            if (code === "AI_BUDGET_EXCEEDED" || code === "AI_UNAVAILABLE") {
              setManualMode(
                code === "AI_BUDGET_EXCEEDED"
                  ? "budget_exceeded"
                  : "ai_unavailable",
              );
              manual.mutate();
            } else {
              setError((err as Error).message);
            }
            setHistory(base); // roll back the optimistic user message
            setIsRunning(false);
          },
        },
      );
    },
    [isRunning, turn, manual],
  );

  const toggleManual = useCallback(() => {
    if (manualMode) {
      setManualMode(null);
    } else {
      setManualMode("manual_toggle");
      manual.mutate();
    }
  }, [manualMode, manual]);

  return {
    history,
    run,
    isRunning,
    error,
    manualMode,
    manualData: manual.data,
    toggleManual,
    usage: turn.data?.usage,
    loading: persisted.isLoading,
  };
}
